import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Divider,
  Stack,
  Paper,
  Button,
  Collapse
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  aggregateByCategory,
  aggregateByCustomer,
  aggregateByProduct,
  formatCurrency
} from '../../utils/salesReportHelpers';

/**
 * Summary Card Component
 */
function SummaryCard({ title, value, subtitle, color = 'primary.main' }) {
  return (
    <Card sx={{ height: '100%', borderTop: `4px solid`, borderColor: color }}>
      <CardContent>
        <Typography color="text.secondary" gutterBottom variant="overline">
          {title}
        </Typography>
        <Typography variant="h4" component="div" sx={{ mb: 1, color }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Category Summary Component
 */
function CategorySummary({ data }) {
  const categoryData = aggregateByCategory(data);
  const categories = Object.values(categoryData).sort((a, b) => b.totalAmount - a.totalAmount);

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Sales by Category
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {categories.map((cat, index) => (
          <Box key={index} sx={{ mb: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box sx={{ flex: 1 }}>
                <Typography variant="body1" fontWeight="medium">
                  {cat.category}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {cat.totalOrders} orders
                </Typography>
              </Box>
              <Typography variant="h6" color="primary.main">
                {formatCurrency(cat.totalAmount)}
              </Typography>
            </Stack>
            {index < categories.length - 1 && <Divider sx={{ mt: 2 }} />}
          </Box>
        ))}
      </CardContent>
    </Card>
  );
}

/**
 * Top Products Component with View All option
 */
function TopProducts({ data }) {
  const [showAll, setShowAll] = useState(false);
  const allProducts = aggregateByProduct(data);
  const displayProducts = showAll ? allProducts : allProducts.slice(0, 10);

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6">
            {showAll ? 'All Products' : 'Top 10 Products'}
          </Typography>
          {allProducts.length > 10 && (
            <Button
              size="small"
              onClick={() => setShowAll(!showAll)}
              endIcon={showAll ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            >
              {showAll ? 'Show Top 10' : `View All (${allProducts.length})`}
            </Button>
          )}
        </Stack>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ maxHeight: showAll ? 600 : 'none', overflow: 'auto' }}>
          {displayProducts.map((product, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" fontWeight="medium">
                    {index + 1}. {product.productName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ID: {product.productId} | Quantity: {product.totalQuantity} | Orders: {product.orderCount}
                  </Typography>
                </Box>
                <Typography variant="body1" color="primary.main" fontWeight="medium">
                  {formatCurrency(product.totalAmount)}
                </Typography>
              </Stack>
              {index < displayProducts.length - 1 && <Divider sx={{ mt: 2 }} />}
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}

/**
 * Top Customers Component with View All option
 */
function TopCustomers({ data }) {
  const [showAll, setShowAll] = useState(false);
  const allCustomers = Object.values(aggregateByCustomer(data))
    .sort((a, b) => b.totalAmount - a.totalAmount);
  const displayCustomers = showAll ? allCustomers : allCustomers.slice(0, 10);

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6">
            {showAll ? 'All Customers' : 'Top 10 Customers'}
          </Typography>
          {allCustomers.length > 10 && (
            <Button
              size="small"
              onClick={() => setShowAll(!showAll)}
              endIcon={showAll ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            >
              {showAll ? 'Show Top 10' : `View All (${allCustomers.length})`}
            </Button>
          )}
        </Stack>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ maxHeight: showAll ? 600 : 'none', overflow: 'auto' }}>
          {displayCustomers.map((customer, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" fontWeight="medium">
                    {index + 1}. {customer.customerName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ID: {customer.customerId} | {customer.totalOrders} orders
                  </Typography>
                </Box>
                <Typography variant="body1" color="primary.main" fontWeight="medium">
                  {formatCurrency(customer.totalAmount)}
                </Typography>
              </Stack>
              {index < displayCustomers.length - 1 && <Divider sx={{ mt: 2 }} />}
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}

/**
 * Main Sales Report Chart/Summary Component
 */
export default function SalesReportChart({ data }) {
  if (!data || data.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          No data available for the selected filters
        </Typography>
      </Box>
    );
  }

  // Calculate summary metrics
  const totalOrders = data.length;
  const totalRevenue = data.reduce((sum, order) => sum + order.totalAmount, 0);
  const averageOrderValue = totalRevenue / totalOrders;
  
  const uniqueCustomers = new Set(data.map(order => order.customerId)).size;
  const categories = Object.keys(aggregateByCategory(data)).length;

  return (
    <Box>
      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Total Orders"
            value={totalOrders}
            subtitle="Orders delivered"
            color="primary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Total Revenue"
            value={formatCurrency(totalRevenue)}
            subtitle="Total sales amount"
            color="success.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Average Order"
            value={formatCurrency(averageOrderValue)}
            subtitle="Per order average"
            color="info.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Customers"
            value={uniqueCustomers}
            subtitle={`${categories} categories`}
            color="warning.main"
          />
        </Grid>
      </Grid>

      {/* Detailed Breakdowns */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <CategorySummary data={data} />
        </Grid>
        <Grid item xs={12} md={4}>
          <TopProducts data={data} />
        </Grid>
        <Grid item xs={12} md={4}>
          <TopCustomers data={data} />
        </Grid>
      </Grid>
    </Box>
  );
}

